/**
 * Comment on s'y rend, et surtout comment on en revient.
 *
 * Deux cas, et un seul mérite notre outil :
 *
 *  - La soirée touche à la nuit. La vraie question n'est alors pas
 *    l'itinéraire mais l'heure limite : le dernier métro ou RER n'est pas le
 *    même pour chacun selon d'où il vient. C'est ce que calcule
 *    meet.getoutpaname.com, et qu'aucune appli d'itinéraire ne fait.
 *
 *  - L'événement est diurne. Tout roule, chacun rentre quand il veut, et une
 *    appli d'itinéraire ordinaire fait mieux le travail que nous. L'y envoyer
 *    serait un détour inutile.
 *
 * Sur les horaires, une mise en garde. Les champs `date_start` / `date_end`
 * du jeu de données portent un suffixe « +00:00 » auquel on ne peut pas se
 * fier : une fiche annoncée « de 10h00 à 12h00 » dans sa description stocke
 * `11:00:00+00:00`. Convertir ces instants donnerait des heures fausses. On
 * lit donc l'horloge telle qu'elle est écrite, et on préfère la description
 * quand elle énonce des horaires — c'est elle que la fiche affiche, et donc
 * ce que le visiteur a sous les yeux.
 */

const MEET_BASE = "https://meet.getoutpaname.com/";

// La fenêtre où se poser la question du retour cesse d'être théorique :
// le métro ferme, et la grande couronne décroche bien avant.
const NUIT_DEBUT = 23 * 60;
const NUIT_FIN = 6 * 60;

// Au-delà, l'événement se répète : l'agenda ne sait pas quel soir on compte
// y aller, autant laisser l'outil proposer sa propre date.
const OCCURRENCE_UNIQUE_MS = 24 * 60 * 60 * 1000;

/** L'horloge telle qu'écrite dans la chaîne ISO, sans conversion. */
function horlogeEcrite(iso) {
  const m = /T(\d{2}):(\d{2})/.exec(iso || "");
  return m ? { h: Number(m[1]), min: Number(m[2]) } : null;
}

/** Le jour tel qu'écrit, "2026-11-07". */
function jourEcrit(iso) {
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(iso || "");
  return m ? m[1] : null;
}

/**
 * Aujourd'hui à Paris, "2026-10-18". Ici la conversion de fuseau est
 * légitime : on part d'un instant réel, pas d'une date du jeu de données.
 * fr-CA formate en ISO.
 */
function aujourdhuiParis() {
  return new Intl.DateTimeFormat("fr-CA", {
    timeZone: "Europe/Paris",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

/**
 * Les créneaux énoncés dans la description.
 *
 * Les formulations varient d'une fiche à l'autre : « de 10h00 à 12h00 »,
 * « Dimanche : 11h00-13h00 », « 20h-2h ». On accepte le « à » comme le
 * tiret, sous ses trois formes.
 *
 * Une série hebdomadaire en énonce souvent plusieurs. On retient celui qui
 * finit le plus tard : s'il existe une seule séance nocturne, la question du
 * retour se pose pour elle.
 */
function horairesDecrits(description) {
  if (!description) return null;
  const texte = String(description).replace(/<[^>]+>/g, " ");
  const motif = /(\d{1,2})\s*h\s*(\d{2})?\s*(?:à|a|-|–|—)\s*(\d{1,2})\s*h\s*(\d{2})?/gi;

  const pad = (v) => String(v).padStart(2, "0");
  let retenu = null;
  for (const m of texte.matchAll(motif)) {
    const hd = Number(m[1]);
    const hf = Number(m[3]);
    if (hd > 23 || hf > 23) continue;
    const creneau = { debut: `${pad(hd)}:${m[2] || "00"}`, fin: `${pad(hf)}:${m[4] || "00"}` };
    // « Finit le plus tard » se juge sur la nuit : 02h00 est plus tardif que
    // 22h00, donc on ramène les petites heures après minuit.
    const poids = (hhmm) => {
      const h = Number(hhmm.slice(0, 2));
      // NUIT_FIN est en minutes : le comparer a des heures rendait la
      // correction universelle, et 16h passait pour plus tardif que 2h.
      return h < NUIT_FIN / 60 ? h + 24 : h;
    };
    if (!retenu || poids(creneau.fin) > poids(retenu.fin)) retenu = creneau;
  }
  return retenu;
}

/**
 * 00:00 et 23:59 ne sont pas des horaires mais les bornes d'une journée,
 * employées quand la vraie plage n'est pas connue. Les lire au pied de la
 * lettre faisait proposer « le retour » sur une séance de piscine du
 * dimanche matin, et sur une quinzaine de l'accessibilité.
 *
 * Elles apparaissent aussi bien dans les champs de date que dans la
 * description, d'où ce filtre appliqué aux deux.
 */
function creneauUtile(debut, fin) {
  const finBidon = fin === "23:59";
  if (debut === "00:00" && (finBidon || !fin)) return { debut: null, fin: null };
  return { debut: debut || null, fin: finBidon ? null : fin || null };
}

function enMinutes(hhmm) {
  const m = /^(\d{2}):(\d{2})$/.exec(hhmm || "");
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}

/**
 * Les horaires à montrer, pris à la meilleure source disponible : la
 * description si elle en énonce, sinon l'horloge écrite dans les champs.
 */
export function horairesEvenement(item) {
  const pad = (v) => String(v).padStart(2, "0");

  const decrits = horairesDecrits(item?.date_description);
  if (decrits) return creneauUtile(decrits.debut, decrits.fin);

  const d = horlogeEcrite(item?.date_start);
  const f = horlogeEcrite(item?.date_end);
  return creneauUtile(
    d ? `${pad(d.h)}:${pad(d.min)}` : null,
    f ? `${pad(f.h)}:${pad(f.min)}` : null
  );
}

/** L'événement empiète-t-il sur la nuit, quand les transports se raréfient ? */
export function finitTard(item) {
  const { debut, fin } = horairesEvenement(item);
  const d = enMinutes(debut);
  const f = enMinutes(fin);

  // Sans heure de fin, une soirée qui commence tard finit forcément la nuit.
  if (d === null) return false;
  if (f === null) return d >= 20 * 60 || d < NUIT_FIN;

  // Passer minuit, c'est la nuit par définition.
  if (f < d) return true;

  return f > NUIT_DEBUT || d < NUIT_FIN;
}

/**
 * L'adresse du calculateur, garnie du lieu et des horaires de l'événement.
 * Null si le lieu n'est pas situé : sans coordonnées, rien à transmettre.
 */
export function lienMeet(item) {
  const lat = item?.lat_lon?.lat;
  const lon = item?.lat_lon?.lon;
  if (typeof lat !== "number" || typeof lon !== "number") return null;

  const p = new URLSearchParams();
  p.set("lieu", item.address_name || item.title || "Le lieu");
  p.set("lat", lat.toFixed(5));
  p.set("lon", lon.toFixed(5));

  const adresse = [
    item.address_street,
    [item.address_zipcode, item.address_city].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ");
  if (adresse) p.set("adresse", adresse);
  if (item.title) p.set("titre", item.title);

  // Une seule occurrence : la date est celle de la soirée. Sur une série,
  // elle désignerait la première, ce qui remplirait l'outil d'à-peu-près.
  const debutMs = Date.parse(item.date_start);
  const finMs = Date.parse(item.date_end);
  const occurrenceUnique =
    Number.isFinite(debutMs) && Number.isFinite(finMs) && finMs - debutMs <= OCCURRENCE_UNIQUE_MS;

  if (occurrenceUnique) {
    // Un événement qui court sur deux jours commence la veille : sa date de
    // début peut être passée alors qu'il a encore lieu ce soir. Transmettre
    // cette date-là ferait répondre au calculateur que la date est révolue,
    // et il refuserait de chercher quoi que ce soit.
    const aujourdhui = aujourdhuiParis();
    const debutJour = jourEcrit(item.date_start);
    const jour = debutJour && debutJour < aujourdhui ? aujourdhui : debutJour;
    const { debut, fin } = horairesEvenement(item);
    if (jour) p.set("date", jour);
    if (debut) p.set("debut", debut);
    if (fin) p.set("fin", fin);
  }

  if (item.id) p.set("retour", `https://getoutpaname.com/event/${item.id}`);

  return `${MEET_BASE}?${p.toString()}`;
}

/** Itinéraires classiques, depuis la position de celui qui clique. */
export function liensItineraire(item) {
  const lat = item?.lat_lon?.lat;
  const lon = item?.lat_lon?.lon;
  if (typeof lat !== "number" || typeof lon !== "number") return null;

  const coord = `${lat.toFixed(5)},${lon.toFixed(5)}`;
  const nom = item.address_name || item.title || "";

  const google = new URLSearchParams({ api: "1", destination: coord, travelmode: "transit" });
  const citymapper = new URLSearchParams({ endcoord: coord });
  if (nom) citymapper.set("endname", nom);

  return {
    google: `https://www.google.com/maps/dir/?${google.toString()}`,
    citymapper: `https://citymapper.com/directions?${citymapper.toString()}`,
  };
}
